import { useEffect, useState } from "react";
import useForm from "antd/es/form/hooks/useForm";
import { IStudentForm } from "@/pages/profile/types/certify";
import { useModalFormStore } from "@/pages/profile/store/modal-form-store";
import type { UploadProps } from "antd/es/upload/interface";
import request, {
  RequestOptions,
} from "project-config/services/request/request";
import { useGlobalStore } from "@/store/GlobalStore";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import { AuthStatus } from "project-config/constants";

export function useStudentForm() {
  const [form] = useForm<IStudentForm>();
  const [imgUrls, setImgUrls] = useState({
    frontImage: "",
    behindImage: "",
    cardImage: "",
  });
  const { setOpenStudentCertifyDrawer } = useModalFormStore();
  const { userInfo, fetchUserData, studentData, fetchStudentCertifyInfo } =
    useGlobalStore();
  const { t: errorT } = useTranslation("error");

  function getUploadProps(name: string) {
    const uploadProps: UploadProps = {
      maxCount: 1,
      accept: "image/jpeg,image/png",
      showUploadList: false,
      beforeUpload(file) {
        if (file.type !== "image/jpeg" && file.type !== "image/png") {
          message.error(errorT("upload_image_format_error"));
          return false;
        }
        if (file.size > 2 * 1024 * 1024) {
          message.error(errorT("upload_image_size_error"));
          return false;
        }
        const url = window.URL.createObjectURL(file);
        setImgUrls({
          ...imgUrls,
          [name]: url,
        });
        form.setFieldValue(name, file);
        form.validateFields();
        return Promise.resolve();
      },
    };
    return uploadProps;
  }

  const uploadFormEvent = (e: any) => {
    return e?.target.files[0];
  };

  const handleCancel = () => {
    setOpenStudentCertifyDrawer(false);
  };

  const deleteImgByField = (field: string) => {
    setImgUrls({
      ...imgUrls,
      [field]: "",
    });
    form.setFieldValue(field, null);
  };

  const handleSubmit = () => {
    form.validateFields().then(async () => {
      const userData = await fetchUserData();
      const hasSubmitEnterpriseCertify =
        userData?.authStatus === AuthStatus.Changing;
      if (hasSubmitEnterpriseCertify) {
        setOpenStudentCertifyDrawer(false);
        return;
      }
      let params = {
        ...form.getFieldsValue(),
        timeAge: form.getFieldValue("timeAgeTemp"),
      };
      if (studentData) {
        params = {
          ...params,
          id: studentData.id,
          remarks: studentData.remarks,
          status: studentData.status,
          userId: userInfo?.userId,
        };
      }
      const formData = new FormData();
      Object.keys(params).forEach((key) => {
        formData.append(key, params[key as keyof typeof params]);
      });
      const { code } = await request.post(
        "/api/rendering/user/addStudentAuthenticationInfo",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        } as RequestOptions
      );
      if (code === 200) {
        message.success(errorT("update_success"));
        fetchStudentCertifyInfo().then(() => {
          fetchUserData();
        });
      } else {
        message.error(errorT(`error_${code}`));
      }
      setOpenStudentCertifyDrawer(false);
    });
  };

  useEffect(() => {
    if (!studentData) {
      form.setFieldsValue({
        timeAgeTemp: new Date().getFullYear(),
      });
      return;
    }
    form.setFieldsValue({
      realName: studentData.realName,
      identitycard: studentData.identitycard,
      schoolAddress: studentData.schoolAddress,
      schoolName: studentData.schoolName,
      educational: studentData.educational,
      timeAgeTemp: studentData.timeAge,
      frontImage: "",
      behindImage: "",
      cardImage: "",
      verify: "",
    });
    setImgUrls({
      behindImage: studentData.identitycardFrontImage,
      frontImage: studentData.identitycardBehindImage,
      cardImage: studentData.studentCardImage,
    });
  }, [studentData, form, userInfo]);

  return {
    form,
    getUploadProps,
    handleCancel,
    handleSubmit,
    imgUrls,
    setImgUrls,
    uploadFormEvent,
    deleteImgByField,
  };
}
